
import { useRef, useState, useEffect } from 'react';

/**
 * Custom hook to manage loading state with proper cleanup
 */
export function useLoadingState() {
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const maxTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeRequestsRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  // Track component mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Start a new request and show loading state
  const startRequest = () => {
    if (!isMountedRef.current) return;
    
    activeRequestsRef.current += 1;
    setLoading(true);
    
    // Clear any existing max timeout when new requests come in
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    
    // Set a failsafe max timeout - prevent infinite loading
    maxTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      
      console.log('Force ending loading state after max timeout');
      setLoading(false);
      activeRequestsRef.current = 0;
      maxTimeoutRef.current = null;
    }, 15000); // 15 seconds max timeout
  };
  
  // End a request and hide loading state after a delay
  const endRequest = () => {
    if (!isMountedRef.current) return;
    
    activeRequestsRef.current = Math.max(0, activeRequestsRef.current - 1);
    
    // If no active requests, set a short delay before hiding the loader
    // This prevents flickering for quick operations
    if (activeRequestsRef.current === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        
        setLoading(false);
        timeoutRef.current = null;
      }, 300);
    }
  };
  
  // Force end all requests - useful for error recovery
  const endAllRequests = () => {
    if (!isMountedRef.current) return;
    
    activeRequestsRef.current = 0;
    setLoading(false);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
  };

  // Clean up any pending timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
      }
    };
  }, []);

  return {
    loading,
    setLoading,
    startRequest,
    endRequest,
    endAllRequests,
    timeoutRef,
    maxTimeoutRef,
    isMountedRef
  };
}

export default useLoadingState;
